// In-browser slideshow renderer. Draws the supplied shots chronologically onto
// a canvas with Ken Burns drift + crossfade transitions, mixes in a
// synthesized laid-back lounge soundtrack (an original mellow groove — see
// startSoundtrack), and records the whole thing to a WebM blob via
// MediaRecorder + canvas.captureStream(). No backend involvement — the image
// bytes are same-origin so the canvas never taints.

import { api } from "../api/client";
import type { Shot } from "../api/types";
import { fmtDate, fmtDateLong, fmtTime } from "../components/format";

const WIDTH = 1280;
const HEIGHT = 720;
const FPS = 30;
// Each image gets its own slide of this length, so a small selection scales at
// 10s per frame (3 images → 30s, 4 → 40s, …).
const SECONDS_PER_IMAGE = 10;
// The clip records in real time, so its runtime *is* the user's wait. Cap it:
// once a selection would exceed this, slides share the budget evenly instead
// of extending the wait. 45s ⇒ 10s/frame up to 4 frames, then compressing.
const MAX_DURATION_S = 45;
// Hard ceiling on frames — bounds image loading + memory. (Total runtime is
// already bounded by MAX_DURATION_S.) Above the cap we sample evenly across
// the chronological set rather than include every frame.
const MAX_FRAMES = 40;
// A stalled image request must never wedge "Gathering frames…" forever.
const IMAGE_LOAD_TIMEOUT_MS = 12_000;

/**
 * Predicted clip length (seconds) for a given shot count — used for dialog
 * copy. {@link SECONDS_PER_IMAGE} per frame, capped at {@link MAX_DURATION_S}
 * so the wait never exceeds it. Actual length may differ slightly if some
 * images fail to load.
 */
export function estimateDurationSeconds(shotCount: number): number {
  const frames = Math.min(Math.max(shotCount, 1), MAX_FRAMES);
  return Math.min(frames * SECONDS_PER_IMAGE, MAX_DURATION_S);
}

// A single shared AudioContext for the whole app. Browsers cap the number of
// live AudioContexts and only let one *start* (resume) from within a user
// gesture. Creating a fresh context after the async frame-loading step lands
// outside that gesture window, leaving resume() pending forever — which is
// exactly what wedged the render on a second run. So we create it lazily from
// a click handler and reuse it across renders, never closing it.
let sharedAudioCtx: AudioContext | null = null;

function audioContextCtor(): typeof AudioContext | null {
  if (typeof window === "undefined") return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext ||
    null
  );
}

/**
 * Get (creating on first call) the shared AudioContext and nudge it to resume.
 * MUST be invoked from within a user gesture (e.g. a click handler) so the
 * browser's autoplay policy allows it to run; safe to call repeatedly. Returns
 * null when WebAudio is unavailable.
 */
export function ensureAudioContext(): AudioContext | null {
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    if (!sharedAudioCtx || sharedAudioCtx.state === "closed") {
      sharedAudioCtx = new Ctor();
    }
    // Fire-and-forget: never await, so a still-suspended context can't block.
    if (sharedAudioCtx.state === "suspended") void sharedAudioCtx.resume();
    return sharedAudioCtx;
  } catch {
    return null;
  }
}

export interface RenderProgress {
  /** "loading" while images download, "recording" during the live capture. */
  phase: "loading" | "recording";
  /** 0..1 within the current phase. */
  progress: number;
}

export interface RenderResult {
  blob: Blob;
  mimeType: string;
  /** Wall-clock length of the produced video, in seconds. */
  durationS: number;
}

export interface RenderHandle {
  promise: Promise<RenderResult>;
  /** Abort the render. Rejects `promise` with a `RenderCancelled` error. */
  cancel: () => void;
}

export class RenderCancelled extends Error {
  constructor() {
    super("Render cancelled");
    this.name = "RenderCancelled";
  }
}

export function isMediaRecordingSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof HTMLCanvasElement.prototype.captureStream === "function"
  );
}

function pickMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm",
  ];
  for (const c of candidates) {
    if (window.MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return "video/webm";
}

/** Evenly down-sample to at most MAX_FRAMES while keeping chronological order. */
function selectFrames(shots: Shot[]): Shot[] {
  const sorted = [...shots].sort(
    (a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime(),
  );
  if (sorted.length <= MAX_FRAMES) return sorted;
  const out: Shot[] = [];
  for (let i = 0; i < MAX_FRAMES; i++) {
    out.push(sorted[Math.round((i * (sorted.length - 1)) / (MAX_FRAMES - 1))]);
  }
  return out;
}

function loadImage(id: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = (result: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    img.decoding = "async";
    img.onload = () => finish(img);
    // A single broken frame shouldn't sink the whole render — skip it.
    img.onerror = () => finish(null);
    // ...and a stalled request must time out rather than hang the loader.
    const timer = setTimeout(() => finish(null), IMAGE_LOAD_TIMEOUT_MS);
    img.src = api.imageContentUrl(id);
  });
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

interface Frame {
  img: HTMLImageElement;
  shot: Shot;
  /** Deterministic Ken Burns pan direction, derived from index parity. */
  panDir: { x: number; y: number };
}

// One second of white noise, reused for every percussion hit. Lazily built
// per sample rate.
let noiseCache: { sampleRate: number; buffer: AudioBuffer } | null = null;
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseCache && noiseCache.sampleRate === ctx.sampleRate) return noiseCache.buffer;
  const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  noiseCache = { sampleRate: ctx.sampleRate, buffer: buf };
  return buf;
}

/**
 * Schedule a laid-back, jazzy lounge groove into `dest` (a
 * MediaStreamAudioDestination) for MediaRecorder to capture, looping to fill the
 * whole clip: a Rhodes-style electric-piano comping seventh chords over a soft
 * round bass, brushed light drums and a sparse mellow lead, with a faint rain
 * texture, through a low-pass + hall reverb — mellow, after-hours background
 * music. This is an original composition, NOT a transcription of any specific
 * song, so it stays license-free. Returns a stop() that tears it down. Audio is
 * *not* routed to the speakers — the sound only lives in the recorded file.
 */
function startSoundtrack(
  ctx: AudioContext,
  dest: MediaStreamAudioDestinationNode,
  durationS: number,
): () => void {
  const now = ctx.currentTime;
  const end = now + durationS;
  const sources: AudioScheduledSourceNode[] = [];

  // Master, kept low (it's background): gentle swell-in / fade-out.
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.linearRampToValueAtTime(0.34, now + 0.6);
  master.gain.setValueAtTime(0.34, Math.max(now + 0.6, end - 1.6));
  master.gain.linearRampToValueAtTime(0.0001, end);
  master.connect(dest);

  // Mellow low-pass — smooth and dark, not bright.
  const warmth = ctx.createBiquadFilter();
  warmth.type = "lowpass";
  warmth.frequency.value = 3000;
  warmth.Q.value = 0.5;
  warmth.connect(master);
  const tone = warmth;

  // Generated hall reverb for late-night space.
  const reverb = ctx.createConvolver();
  const irLen = Math.floor(ctx.sampleRate * 2.8);
  const ir = ctx.createBuffer(2, irLen, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    for (let i = 0; i < irLen; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / irLen, 2.4);
    }
  }
  reverb.buffer = ir;
  const reverbWet = ctx.createGain();
  reverbWet.gain.value = 0.3;
  reverb.connect(reverbWet).connect(master);
  const reverbSend = ctx.createGain();
  reverbSend.gain.value = 0.5;
  reverbSend.connect(reverb);
  warmth.connect(reverbSend);

  const drumBus = ctx.createGain();
  drumBus.gain.value = 0.8;
  drumBus.connect(master);
  drumBus.connect(reverbSend);

  // Semitones above middle C (C4, 261.63 Hz) → frequency, equal temperament.
  const f = (semi: number) => 261.6256 * Math.pow(2, semi / 12);

  // Faint rain texture — a looping noise band that slowly breathes.
  {
    const rain = ctx.createBufferSource();
    rain.buffer = noiseBuffer(ctx);
    rain.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 5000;
    bp.Q.value = 0.4;
    const rg = ctx.createGain();
    rg.gain.value = 0.03;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.06;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.012;
    lfo.connect(lfoDepth).connect(rg.gain);
    rain.connect(bp).connect(rg).connect(master);
    rain.start(now);
    rain.stop(end);
    lfo.start(now);
    lfo.stop(end);
    sources.push(rain, lfo);
  }

  // Hypnotic two-bar minor vamp (A Dorian feel): Am7 → Dm7. Chord voicings as
  // semitones above C4, with the bass root + fifth underneath.
  const chords = [
    [0, 4, 7, 9], // Am7  (C E G A)
    [0, 2, 5, 9], // Dm7  (C D F A)
  ];
  const bassRoot = [-15, -10]; // A2, D3
  const bassFifth = [-8, -3]; // E3, A3
  // Sparse, original lead phrase over the 2-bar loop: [semi above C4, startBeat,
  // beats]. Leaves plenty of space so it stays background.
  const leadMotif: [number, number, number][] = [
    [9, 0, 1.5], [7, 1.5, 0.5], [4, 2.5, 1.5],
    [2, 4.5, 1], [4, 5.5, 0.5], [0, 6, 2],
  ];

  const BPM = 84; // slow, relaxed
  const beat = 60 / BPM;
  const barDur = 4 * beat;
  const loopDur = 2 * barDur;

  // Pitched note with a smooth envelope into `target`.
  const note = (
    type: OscillatorType,
    freq: number,
    start: number,
    dur: number,
    peak: number,
    target: AudioNode,
    attack = 0.012,
    release = 0.2,
    detune = 0,
  ) => {
    if (start >= end) return;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.detune.value = detune;
    const g = ctx.createGain();
    const sustainAt = Math.max(start + attack, start + dur - release);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(peak, start + attack);
    g.gain.setValueAtTime(peak, sustainAt);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    o.connect(g).connect(target);
    o.start(start);
    o.stop(start + dur + 0.05);
    sources.push(o);
  };

  // Electric-piano voice: triangle body + a soft sine octave for the bell tine.
  const ep = (semi: number, start: number, dur: number, level: number) => {
    note("triangle", f(semi), start, dur, level, tone, 0.008, 0.25, -4);
    note("sine", f(semi + 12), start, dur * 0.8, level * 0.4, tone, 0.008, 0.25, 4);
  };

  // Soft kick.
  const kick = (start: number) => {
    if (start >= end) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(95, start);
    o.frequency.exponentialRampToValueAtTime(48, start + 0.12);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(0.5, start + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.25);
    o.connect(g).connect(drumBus);
    o.start(start);
    o.stop(start + 0.28);
    sources.push(o);
  };

  // Filtered noise hit — brushed snare (low-pass) or soft hat (high-pass).
  const brushHit = (
    start: number,
    dur: number,
    type: BiquadFilterType,
    freq: number,
    level: number,
  ) => {
    if (start >= end) return;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    const filt = ctx.createBiquadFilter();
    filt.type = type;
    filt.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(level, start + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    src.connect(filt).connect(g).connect(drumBus);
    src.start(start);
    src.stop(start + dur + 0.02);
    sources.push(src);
  };
  const brush = (start: number) => brushHit(start, 0.22, "lowpass", 2600, 0.16);
  const hat = (start: number, level: number) => brushHit(start, 0.04, "highpass", 9000, level);

  // Loop the 2-bar vamp until the clip is full; the lead drifts in after the
  // first loop so the opening stays spacious.
  for (let rep = 0; ; rep++) {
    const loopStart = now + rep * loopDur;
    if (loopStart >= end) break;
    const withLead = rep >= 1;

    for (let bar = 0; bar < 2; bar++) {
      const barStart = loopStart + bar * barDur;
      if (barStart >= end) break;
      const ch = chords[bar];

      // EP comping: a held downbeat chord + a softer syncopated stab.
      for (const st of ch) ep(st, barStart, barDur * 0.92, 0.07);
      for (const st of ch) ep(st, barStart + 1.5 * beat, barDur * 0.4, 0.045);

      // Round bass: root for the first half, fifth for the second.
      note("triangle", f(bassRoot[bar]), barStart, 2 * beat * 0.95, 0.3, tone, 0.02, 0.12);
      note("triangle", f(bassFifth[bar]), barStart + 2 * beat, 2 * beat * 0.95, 0.26, tone, 0.02, 0.12);

      // Light brushed kit, half-time and quiet.
      kick(barStart);
      brush(barStart + 2 * beat);
      for (let b = 0; b < 4; b++) {
        hat(barStart + b * beat, 0.035);
        hat(barStart + (b + 0.5) * beat, 0.02);
      }
    }

    if (withLead) {
      for (const [semi, sb, db] of leadMotif) {
        const ls = loopStart + sb * beat;
        if (ls >= end) break;
        note("triangle", f(semi), ls, db * beat * 0.9, 0.1, tone, 0.02, 0.18, -3);
        note("sine", f(semi + 12), ls, db * beat * 0.9, 0.04, tone, 0.02, 0.18);
      }
    }
  }

  return () => {
    for (const s of sources) {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    }
  };
}

export function renderSlideshow(
  shots: Shot[],
  opts: {
    onProgress?: (p: RenderProgress) => void;
    /**
     * AudioContext to mix the soundtrack into. Pass one primed from a user
     * gesture (see {@link ensureAudioContext}); omit and we fall back to the
     * shared context. Never closed here — it outlives the render.
     */
    audioContext?: AudioContext;
  } = {},
): RenderHandle {
  let cancelled = false;
  let rafId = 0;
  let recorder: MediaRecorder | null = null;
  let stopAudio: (() => void) | null = null;

  const cleanup = () => {
    if (rafId) cancelAnimationFrame(rafId);
    // Stop the soundtrack nodes, but leave the (shared) AudioContext open.
    stopAudio?.();
    stopAudio = null;
  };

  const promise = new Promise<RenderResult>((resolve, reject) => {
    void (async () => {
      if (!isMediaRecordingSupported()) {
        reject(new Error("Video recording isn't supported in this browser."));
        return;
      }

      const selected = selectFrames(shots);
      if (selected.length === 0) {
        reject(new Error("There are no images to animate."));
        return;
      }

      // ── Phase 1: preload every frame. ──
      const frames: Frame[] = [];
      for (let i = 0; i < selected.length; i++) {
        if (cancelled) {
          reject(new RenderCancelled());
          return;
        }
        const img = await loadImage(selected[i].imageId);
        if (img) {
          frames.push({
            img,
            shot: selected[i],
            panDir: { x: i % 2 === 0 ? 1 : -1, y: i % 3 === 0 ? 1 : -1 },
          });
        }
        opts.onProgress?.({ phase: "loading", progress: (i + 1) / selected.length });
      }

      if (cancelled) {
        reject(new RenderCancelled());
        return;
      }
      if (frames.length === 0) {
        reject(new Error("None of the images could be loaded."));
        return;
      }

      // ── Set up canvas + audio + recorder. ──
      const canvas = document.createElement("canvas");
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      const ctx = canvas.getContext("2d", { alpha: false });
      if (!ctx) {
        reject(new Error("Could not create a drawing surface."));
        return;
      }

      const titleRange =
        frames.length > 1
          ? `${fmtDate(new Date(frames[0].shot.capturedAt))} – ${fmtDate(
              new Date(frames[frames.length - 1].shot.capturedAt),
            )}`
          : fmtDateLong(new Date(frames[0].shot.capturedAt));

      const n = frames.length;
      // 10s per image, but never longer than the wait ceiling — past that,
      // slides share the budget evenly so the render (and wait) stays bounded.
      const durationS = Math.min(n * SECONDS_PER_IMAGE, MAX_DURATION_S);
      const slide = durationS / n;
      const fade = Math.min(slide * 0.5, 1.0);

      const drawCover = (
        img: HTMLImageElement,
        scale: number,
        panX: number,
        panY: number,
        alpha: number,
      ) => {
        const ir = img.width / img.height;
        const cr = WIDTH / HEIGHT;
        let dw: number;
        let dh: number;
        if (ir > cr) {
          dh = HEIGHT * scale;
          dw = dh * ir;
        } else {
          dw = WIDTH * scale;
          dh = dw / ir;
        }
        // Clamp pan to the overflow so we never expose the letterbox.
        const maxX = (dw - WIDTH) / 2;
        const maxY = (dh - HEIGHT) / 2;
        const dx = (WIDTH - dw) / 2 + clamp(panX, -maxX, maxX);
        const dy = (HEIGHT - dh) / 2 + clamp(panY, -maxY, maxY);
        ctx.globalAlpha = alpha;
        ctx.drawImage(img, dx, dy, dw, dh);
        ctx.globalAlpha = 1;
      };

      const drawFrame = (frame: Frame, p: number, alpha: number) => {
        // Slow zoom-in (1.06 → 1.16) plus a gentle pan along panDir.
        const scale = 1.06 + 0.1 * p;
        const drift = 60 * p;
        drawCover(frame.img, scale, frame.panDir.x * drift, frame.panDir.y * drift, alpha);
      };

      const drawVignette = () => {
        const g = ctx.createRadialGradient(
          WIDTH / 2,
          HEIGHT / 2,
          HEIGHT * 0.35,
          WIDTH / 2,
          HEIGHT / 2,
          HEIGHT * 0.85,
        );
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(1, "rgba(8,11,9,0.55)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, WIDTH, HEIGHT);
      };

      const drawCaption = (frame: Frame, opacity: number) => {
        if (opacity <= 0.01) return;
        const d = new Date(frame.shot.capturedAt);
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = "#ece6d6";
        ctx.font = "600 30px Georgia, 'Times New Roman', serif";
        ctx.fillText(`${fmtDate(d)}`, 56, HEIGHT - 70);
        ctx.fillStyle = "rgba(236,230,214,0.72)";
        ctx.font = "18px ui-monospace, Menlo, monospace";
        ctx.fillText(
          `${fmtTime(d)}  ·  ${frame.shot.cameraId.toUpperCase()}`,
          56,
          HEIGHT - 44,
        );
        // Ember tick, echoing the app accent.
        ctx.fillStyle = "#d97757";
        ctx.fillRect(56, HEIGHT - 96, 30, 3);
        ctx.restore();
      };

      const drawTitleCard = (opacity: number) => {
        if (opacity <= 0.01) return;
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.fillStyle = "rgba(8,11,9,0.45)";
        ctx.fillRect(0, 0, WIDTH, HEIGHT);
        ctx.textAlign = "center";
        ctx.fillStyle = "#ece6d6";
        ctx.font = "italic 600 60px Georgia, 'Times New Roman', serif";
        ctx.fillText("In the woods", WIDTH / 2, HEIGHT / 2 - 4);
        ctx.fillStyle = "rgba(236,230,214,0.78)";
        ctx.font = "20px ui-monospace, Menlo, monospace";
        ctx.fillText(titleRange.toUpperCase(), WIDTH / 2, HEIGHT / 2 + 40);
        ctx.fillStyle = "#d97757";
        ctx.fillRect(WIDTH / 2 - 24, HEIGHT / 2 - 64, 48, 3);
        ctx.restore();
        ctx.textAlign = "left";
      };

      const drawAt = (t: number) => {
        ctx.fillStyle = "#0c100d";
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        const i = Math.min(n - 1, Math.floor(t / slide));
        const localT = t - i * slide;
        const p = clamp(localT / slide, 0, 1);
        drawFrame(frames[i], p, 1);

        // Crossfade into the next frame during the slide's tail.
        if (i < n - 1 && localT > slide - fade) {
          const f = easeInOut((localT - (slide - fade)) / fade);
          drawFrame(frames[i + 1], 0, f);
        }

        drawVignette();

        // Caption fades in early, out late within each slide.
        const capIn = clamp(localT / 0.6, 0, 1);
        const capOut = clamp((slide - localT) / 0.6, 0, 1);
        drawCaption(frames[i], Math.min(capIn, capOut));

        // Title card over the first ~2.2s.
        if (t < 2.2) drawTitleCard(easeInOut(clamp((2.2 - t) / 0.6, 0, 1)));
      };

      // Paint frame 0 before recording starts so there's no black flash.
      drawAt(0);

      const stream = canvas.captureStream(FPS);

      // Reuse the gesture-primed shared context — never create one here, after
      // the awaits, where resume() would fall outside the user gesture and
      // could hang the whole pipeline. We also never await resume(): a
      // briefly-suspended context yields a short silent lead-in, not a freeze.
      const audioCtx = opts.audioContext ?? ensureAudioContext();
      if (audioCtx && audioCtx.state !== "closed") {
        try {
          if (audioCtx.state === "suspended") void audioCtx.resume();
          const audioDest = audioCtx.createMediaStreamDestination();
          stopAudio = startSoundtrack(audioCtx, audioDest, durationS);
          for (const track of audioDest.stream.getAudioTracks()) stream.addTrack(track);
        } catch {
          // Audio is a nice-to-have; a silent video still beats a hard failure.
          stopAudio?.();
          stopAudio = null;
        }
      }

      if (cancelled) {
        cleanup();
        reject(new RenderCancelled());
        return;
      }

      const mimeType = pickMimeType();
      const chunks: BlobPart[] = [];
      recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5_000_000,
      });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder.onerror = () => {
        cleanup();
        reject(new Error("Recording failed."));
      };
      recorder.onstop = () => {
        cleanup();
        if (cancelled) {
          reject(new RenderCancelled());
          return;
        }
        resolve({
          blob: new Blob(chunks, { type: mimeType }),
          mimeType,
          durationS,
        });
      };

      recorder.start();
      const startT = performance.now();

      const tick = (nowMs: number) => {
        if (cancelled) {
          if (recorder && recorder.state !== "inactive") recorder.stop();
          else {
            cleanup();
            reject(new RenderCancelled());
          }
          return;
        }
        // The first rAF can carry a timestamp from a frame that began just
        // before startT, yielding a tiny negative t (→ frame index -1). Clamp
        // the timeline to [0, durationS] so indexing is always in range.
        const t = Math.max(0, (nowMs - startT) / 1000);
        drawAt(Math.min(t, durationS));
        opts.onProgress?.({ phase: "recording", progress: clamp(t / durationS, 0, 1) });
        if (t >= durationS) {
          if (recorder && recorder.state !== "inactive") recorder.stop();
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    })().catch((err) => {
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    });
  });

  return {
    promise,
    cancel: () => {
      cancelled = true;
    },
  };
}
